import React from 'react';
import PropTypes from 'prop-types';
import { Card } from 'common/Card';
import formatDate from 'common/utils/Dates/formatDate';
import AssignUserToAuth from '../../components/AssignUserToAuth';
import ServiceAuthorizationStatusIndicator from '../../components/ServiceAuthorizationStatusIndicator';
import AuthorizationSummarySection from './AuthorizationSummarySection';
import FeeScheduleProgramSection from './FeeScheduleProgramSection';

const VerticalDivider = () => <div className="border-l border-dark-fill-blue border-solid" />;

const AuthorizationDetailsCard = ({
  authorization,
  feeScheduleProgram,
  dateRequested,
  dateRequestedStartsAt,
  dateRequestedEndsAt,
  isSingleDateRequested,
  isFspLoaded,
  isCostBased,
}) => (
  <Card className="bg-white overflow-visible" data-testid="auth-details-card">
    <div className="px-6 flex justify-between">
      <div className="py-4">
        <h1 className="text-text-blue font-heavy-font text-2xl leading-8">
          Authorization Request #{authorization.short_id}
        </h1>
        <div className="flex space-x-6 items-center">
          <p className="text-text-blue text-sm leading-5">Last updated: {formatDate(authorization.updated_at)}</p>
          <ServiceAuthorizationStatusIndicator
            status={authorization.state}
            autoApproved={authorization.auto_approved}
          />
        </div>
      </div>
      <div className="py-4">
        <AssignUserToAuth
          authorizationId={authorization.id}
          currentAssigneeId={authorization.assignee?.id}
          state={authorization.state}
        />
      </div>
    </div>
    <div className="flex flex-col py-4 bg-gray-100">
      <div className="flex px-6 pb-4 space-x-12">
        <div className="w-1/2">
          <AuthorizationSummarySection
            authorization={authorization}
            feeScheduleProgram={feeScheduleProgram}
            dateRequested={dateRequested}
            dateRequestedStartsAt={dateRequestedStartsAt}
            dateRequestedEndsAt={dateRequestedEndsAt}
            isSingleDateRequested={isSingleDateRequested}
            isFspLoaded={isFspLoaded}
          />
        </div>
        <VerticalDivider />
        <div className="w-1/2">
          <dl className="m-0 grid grid-cols-2 gap-x-8 gap-y-2">
            <FeeScheduleProgramSection
              feeScheduleProgram={feeScheduleProgram}
              isLoaded={isFspLoaded}
              isCostBased={isCostBased}
            />
          </dl>
        </div>
      </div>
    </div>
  </Card>
);

AuthorizationDetailsCard.propTypes = {
  authorization: PropTypes.shape({
    id: PropTypes.string.isRequired,
    short_id: PropTypes.string.isRequired,
    updated_at: PropTypes.string.isRequired,
    state: PropTypes.string.isRequired,
    auto_approved: PropTypes.bool,
    requested_unit_amount: PropTypes.number,
    requested_cents: PropTypes.number,
    clinical_modifications: PropTypes.arrayOf(PropTypes.shape({
      code: PropTypes.string,
    })),
    requester: PropTypes.shape({
      full_name: PropTypes.string,
      provider: PropTypes.shape({
        name: PropTypes.string,
      }),
    }),
    assignee: PropTypes.shape({
      id: PropTypes.string,
    }),
  }).isRequired,
  feeScheduleProgram: PropTypes.shape({
    unit: PropTypes.string,
    name: PropTypes.string,
    payment_type: PropTypes.string,
    unit_rate: PropTypes.number,
    cap_information: PropTypes.string,
    cm_code_required_on_authorization: PropTypes.bool,
  }).isRequired,
  dateRequested: PropTypes.string,
  dateRequestedStartsAt: PropTypes.string.isRequired,
  dateRequestedEndsAt: PropTypes.string.isRequired,
  isSingleDateRequested: PropTypes.bool.isRequired,
  isFspLoaded: PropTypes.bool.isRequired,
  isCostBased: PropTypes.bool.isRequired,
};

AuthorizationDetailsCard.defaultProps = {
  dateRequested: null,
};

export default AuthorizationDetailsCard;
